import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ApiOperationStatus } from '../../../../shared/data-model/models/api-operation-status.model';
import { ApiGatewayService } from '../../../../shared/services/api-gateway/api-gateway.service';
import { CollectionUtils } from '../../../../shared/utils/collection-utils';
import { CommonUtils } from '../../../../shared/utils/common-utils';
import { OssOptional } from '../../../../shared/utils/oss-optional';
import { ECrashValidationErrorUiProps } from '../../../data-model/models/c-crash-validation-error.model';
import { ECrashValidation } from '../../../data-model/models/e-crash-validation.model';
import { buildRuleIdValidationErrorFunctionMap } from './e-crash.helpers';

export type UiPath = [string, string, string, string];
export type KeyPath = [string, number, string, number, string];

@Injectable({ providedIn: 'root' })
export class ECrashService {
  private ruleIdValidationErrorFunctionMap: Map<string, (...args: unknown[]) => ECrashValidationErrorUiProps>;

  constructor(private readonly apiGateway: ApiGatewayService) {
    this.ruleIdValidationErrorFunctionMap = buildRuleIdValidationErrorFunctionMap();
  }

  validateReport(crashNumber: string): Observable<ECrashValidation> {
    return this.apiGateway.post<ECrashValidation>('oss/eCrashAPI/validateReport', { crash_num: crashNumber }).pipe(map(x => this.mapECrashValidation(x)));
  }

  submitReport(crashNumber: string, reviewingOfficer: { firstName: string; lastName: string; rank: string }): Observable<ApiOperationStatus> {
    return this.apiGateway.post('oss/eCrashAPI/submitReport', { crash_num: crashNumber, reviewing_officer: reviewingOfficer });
  }

  private mapECrashValidation(eCrashValidation: ECrashValidation): ECrashValidation {
    // instantiate a new object to avoid mutating the original object
    // filter out validation errors that do not have a rule id
    // when a report is valid, there will be two validation errors: no warnings found and no errors found
    const updatedECrashValidation = {
      ...eCrashValidation,
      validationErrors: eCrashValidation.validationErrors
        .filter(validationError => CommonUtils.isDefined(validationError.RuleId))
        .map(validationError => {
          const validationErrorClone = structuredClone(validationError);

          const numberIndexes = [...validationErrorClone.Text.matchAll(/\[?(\d+)\]?/g)].map(match => {
            const fullMatch = match[0];
            const value = parseInt(match[1], 10);
            return fullMatch.startsWith('[') ? value + 1 : value;
          });

          const split = validationErrorClone.RuleId.split('.');
          const lastRuleToken = split[split.length - 1];

          // replace [i] with [i+1] cause i is zero based
          validationErrorClone.Text = validationErrorClone.Text.replace(/\[(\d+)\]/g, (_, i) => `[${+i + 1}]`);

          OssOptional.ofNullable(this.ruleIdValidationErrorFunctionMap.get(lastRuleToken))
            .map(validationErrorFunction => validationErrorFunction(...numberIndexes))
            .ifPresent(validationErrorData => {
              const [schemaGroupIndex = null, subGroupIndex = null] = numberIndexes ?? [];

              validationErrorClone.uiPath = OssOptional.ofNullable(validationErrorData.uiPath).orElseNull();
              validationErrorClone.keyPath = OssOptional.ofNullable(validationErrorData.keyPath).orElseNull();

              validationErrorClone.shortDescription = OssOptional.ofNullable(validationErrorData.shortDescription).orElseNull();
              validationErrorClone.longDescription = OssOptional.ofNullable(validationErrorData.longDescription).orElseNull();

              validationErrorClone.schemaGroupRepeats = OssOptional.ofNullable(validationErrorData.schemaGroupRepeats).orElse(false);
              validationErrorClone.schemaGroupIndex = OssOptional.ofNullable(schemaGroupIndex)
                .map(schemaGroupIndex => schemaGroupIndex - 1)
                .orElse(0);
              validationErrorClone.subGroupRepeats = OssOptional.ofNullable(validationErrorData.subGroupRepeats).orElse(false);
              validationErrorClone.subGroupIndex = OssOptional.ofNullable(subGroupIndex)
                .map(subGroupIndex => subGroupIndex - 1)
                .orElse(0);

              validationErrorClone.hasDownstreamValueChanges = OssOptional.ofNullable(validationErrorData.hasDownstreamValueChanges).orElse(false);
            });

          return validationErrorClone;
        }),
    };

    // if there are no format errors and there is one validation error and it is DiagramsMustNotBeEmpty, then remove it and mark the report as valid
    if (
      CommonUtils.isEmpty(updatedECrashValidation.formatErrors) &&
      CommonUtils.hasContent(updatedECrashValidation.validationErrors) &&
      updatedECrashValidation.validationErrors.length === 1 &&
      CollectionUtils.getOnlyElement(updatedECrashValidation.validationErrors).RuleId.includes('DiagramsMustNotBeEmpty')
    ) {
      updatedECrashValidation.validationErrors = [];
      updatedECrashValidation.isValid = true;
    }

    // if there are more than one validation errors and one of them is DiagramsMustNotBeEmpty, then remove it
    else if (CommonUtils.hasContent(updatedECrashValidation.validationErrors) && updatedECrashValidation.validationErrors.length > 1) {
      updatedECrashValidation.validationErrors = updatedECrashValidation.validationErrors.filter(
        validationError => !validationError.RuleId.includes('DiagramsMustNotBeEmpty')
      );
    }

    return updatedECrashValidation;
  }
}
